/**
 * Extracts Google file references (Docs/Sheets/Slides/Drive) from an assistant
 * message body so they render as inline iframe previews instead of plain links.
 *
 * The Google agents surface a created/edited file as an ordinary **markdown
 * link** — e.g. `📄 **[Clawbert Test Document](https://docs.google.com/.../edit)**`
 * — NOT a `[INTEGRATION_LINK]` tag. So detection runs on the message content
 * itself (mirroring `extractResolverOptions`), in two passes:
 *
 *   1. Markdown links `[label](url)` whose url is a Google file → collect the
 *      embed and *delinkify* (replace with the bare label text) so surrounding
 *      markdown (emoji, bold) survives and the title still reads in the prose.
 *   2. Bare Google file URLs left in the text → collect the embed and strip the
 *      URL (a kind-based label like "Google Doc" is used for the card).
 *
 * Non-Google links/URLs are left untouched. Split from MessageBubble.tsx so the
 * parser is unit-testable without the component (react-refresh enforcement).
 */

import { detectGoogleEmbed, type GoogleEmbed, type GoogleFileKind } from "@domain/google-embed";

export interface ChatGoogleEmbed {
  readonly embed: GoogleEmbed;
  readonly label: string;
}

const MD_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const BARE_URL_RE = /https?:\/\/[^\s<>)"']+/g;

const KIND_LABEL: Record<GoogleFileKind, string> = {
  doc: "Google Doc",
  sheets: "Google Sheet",
  slides: "Google Slides",
  drive: "Google Drive file",
};

export function extractGoogleEmbeds(content: string): {
  cleanContent: string;
  embeds: ChatGoogleEmbed[];
} {
  const embeds: ChatGoogleEmbed[] = [];

  // Pass 1 — markdown links. Keep the label as plain text in the prose.
  let out = content.replace(MD_LINK_RE, (match, label: string, url: string) => {
    const embed = detectGoogleEmbed(url);
    if (!embed) return match;
    embeds.push({ embed, label });
    return label;
  });

  // Pass 2 — bare Google URLs (any markdown-linked Google URL is already gone).
  out = out.replace(BARE_URL_RE, (match) => {
    const embed = detectGoogleEmbed(match);
    if (!embed) return match;
    embeds.push({ embed, label: KIND_LABEL[embed.kind] });
    return "";
  });

  return { cleanContent: out.trim(), embeds };
}
